import React, { useState, useEffect, useRef, useCallback } from 'react';
import TagChip from './TagChip';
import { fetchVocabulary, usageCounts, matchTags, nearMatch, normalizeTagName, createTag, TAG_MAX_LEN, TAG_DEFAULT_COLOUR } from '../lib/tags';

// Anchored tag-picker popover. The parent renders a relative wrapper with the
// "+ Add tag" trigger and mounts <TagPicker> when open. Live-filters the
// vocabulary client-side; create requires a deliberate button click (Enter
// never creates). Multi-select: stays open until outside-click / Esc.
//
// Props:
//   vocabulary   'supplier' | 'brief'
//   appliedTags  array of tag objects currently on the record (excluded from list)
//   onApply(tag) apply an existing/created tag to the record
//   onClose()    close the popover
//   actorId      current internal user id (for created_by)
//   align        'left' | 'right' (popover horizontal anchor; default left)
export default function TagPicker({ vocabulary, appliedTags = [], onApply, onClose, actorId, align = 'left' }) {
  const [vocab, setVocab] = useState([]);
  const [usage, setUsage] = useState(new Map());
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [v, u] = await Promise.all([
        fetchVocabulary(vocabulary, { includeArchived: false }),
        usageCounts(vocabulary),
      ]);
      if (cancelled) return;
      setVocab(v);
      setUsage(u);
    })();
    return () => { cancelled = true; };
  }, [vocabulary]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on outside click / Esc.
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const appliedIds = new Set(appliedTags.map(t => t.id));
  const available = vocab.filter(t => !appliedIds.has(t.id));
  const clean = normalizeTagName(query);
  const lcq = clean.toLowerCase();

  const exact = clean ? vocab.find(t => t.name.toLowerCase() === lcq) : null;
  const exactApplied = exact ? appliedIds.has(exact.id) : false;
  const matches = clean ? matchTags(query, available, usage) : [];
  const near = clean && matches.length === 0 && !exact ? nearMatch(query, available) : null;
  const blocked = !!exact; // exact (case-insensitive) duplicate -> creation blocked

  const handleCreate = useCallback(async () => {
    if (!clean || blocked || creating) return;
    setCreating(true);
    setError(null);
    // Default new picker-created tags onto the tag-safe palette. Passing
    // the legacy 'neutral' enum string here would fail post-2026-05 hex
    // schema; admin can recolour from AdminTags later if needed.
    const { data, error: err } = await createTag({ name: clean, vocabulary, colour: TAG_DEFAULT_COLOUR, actorId });
    setCreating(false);
    if (err) { setError(err.message || 'Could not create tag'); return; }
    setVocab(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    onApply?.(data);
    setQuery('');
  }, [clean, blocked, creating, vocabulary, actorId, onApply]);

  const idle = !clean;

  return (
    <div
      ref={ref}
      className={`absolute z-30 mt-2 w-80 bg-white border border-line rounded-xl p-3.5 shadow-[0_12px_32px_rgba(0,0,0,0.5)] ${align === 'right' ? 'right-0' : 'left-0'}`}
    >
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value.slice(0, TAG_MAX_LEN)); setError(null); }}
        placeholder="Search or create a tag…"
        className={`w-full bg-white border rounded-md px-3 py-2 text-xs text-ink outline-none mb-3 font-['Metropolis'] ${blocked && !exactApplied ? 'border-[#E0290A]' : clean ? 'border-[#92D400]' : 'border-line'}`}
      />

      {/* Available / matching list */}
      {idle ? (
        <>
          <p className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-2 font-['Metropolis']">Available</p>
          {available.length === 0 ? (
            <p className="text-[11px] text-[#51534A] italic">No tags yet — type to create the first.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[...available]
                .sort((a, b) => (usage.get(b.id) || 0) - (usage.get(a.id) || 0))
                .slice(0, 20)
                .map(t => <TagChip key={t.id} tag={t} onClick={() => onApply?.(t)} />)}
            </div>
          )}
          <p className="text-[11px] text-[#51534A] pt-3 border-t border-line font-['Metropolis']">Start typing to find or create a tag</p>
        </>
      ) : (
        <>
          {(matches.length > 0 || exact) && (
            <>
              <p className={`text-[9px] font-bold tracking-[0.18em] uppercase mb-2 font-['Metropolis'] ${blocked && !exactApplied ? 'text-fusion-forest' : 'text-fusion-forest'}`}>
                Matching tags ({exact && matches.length === 0 ? 1 : matches.length})
              </p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {exact && matches.length === 0 && !exactApplied && (
                  <TagChip tag={exact} onClick={() => onApply?.(exact)} />
                )}
                {matches.map(m => (
                  <TagChip key={m.tag.id} tag={m.tag} highlight={{ start: m.start, len: m.len }} onClick={() => onApply?.(m.tag)} />
                ))}
              </div>
            </>
          )}

          {/* Near-match suggestion (no substring match, but something close) */}
          {near && (
            <div className="pt-3 border-t border-line mb-2">
              <p className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#7A4E10] mb-1.5 font-['Metropolis']">Did you mean</p>
              <button
                type="button"
                onClick={() => onApply?.(near)}
                className="w-full text-left px-2.5 py-1.5 bg-[#E89A2E]/[0.06] border border-[#E89A2E]/30 rounded-md text-[11px] text-fusion-mid-grey hover:bg-[#E89A2E]/10 font-['Metropolis']"
              >
                Use existing tag: <strong className="text-ink">{near.name}</strong> ({usage.get(near.id) || 0} {vocabulary === 'supplier' ? 'suppliers' : 'briefs'})
              </button>
            </div>
          )}

          {/* Create / blocked footer */}
          <div className="pt-3 border-t border-line">
            {blocked ? (
              exactApplied ? (
                <p className="px-3 py-2 bg-white border border-line rounded-md text-[11px] text-[#51534A] font-['Metropolis']">
                  <strong className="text-fusion-mid-grey">"{exact.name}"</strong> is already applied to this record.
                </p>
              ) : (
                <p className="px-3 py-2 bg-[#E0290A]/[0.06] border border-[#E0290A]/30 rounded-md text-[11px] text-[#E0290A] font-['Metropolis']">
                  <strong className="text-ink">"{exact.name}" already exists.</strong> Click it above to apply.
                </p>
              )
            ) : (
              <div className="flex items-center justify-between px-3 py-2 bg-white border border-line rounded-md">
                <span className="text-[11px] text-fusion-mid-grey font-['Metropolis'] truncate">Or create new: <span className="text-fusion-forest font-extrabold">{clean}</span></span>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-[#92D400] text-[#131313] rounded-full px-3 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase font-['Metropolis'] hover:bg-[#a4e810] transition-colors disabled:opacity-50"
                >
                  {creating ? '…' : 'Create'}
                </button>
              </div>
            )}
            {error && <p className="text-[11px] text-[#E0290A] mt-2 font-['Metropolis']">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
}
